/**
 * Google One Tap（GSI / FedCM）でのログイン。
 * プロンプト自体は Google がブラウザ上に描画するため自前 UI は無い。
 * 取得した ID トークンを Firebase Auth の credential に変換してログインする。
 * 表示のオーケストレーション（前回ログインとの使い分け）は LastLoginRecommendCard 側。
 *
 * 前提: OAuth Web クライアントの「承認済み JavaScript 生成元」に配信オリジンが
 * 登録されていること（未登録オリジンでは prompt が無言で不発になる）。
 */
import { watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useLoginPostProcess } from '@/composables/useLoginPostProcess'
import {
  createGoogleCredential,
  getFirebaseAuth,
  signInWithCredential
} from '@/infra/firebase/auth'
import { getFirebaseClient } from '@/plugins/firebase-lib'

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

type PromptMomentNotification = {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
  isDismissedMoment?: () => boolean
}

type GoogleAccountsId = {
  initialize: (config: Record<string, unknown>) => void
  prompt: (callback?: (notification: PromptMomentNotification) => void) => void
  disableAutoSelect: () => void
}

function getGoogleAccountsId(): GoogleAccountsId | null {
  const w = window as unknown as {
    google?: { accounts?: { id?: GoogleAccountsId } }
  }
  return w.google?.accounts?.id ?? null
}

let scriptPromise: Promise<void> | null = null

function loadGsiScript(): Promise<void> {
  if (getGoogleAccountsId()) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GSI_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('GSI script load failed'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

type GtmFacade = {
  hintAuthMethod?: (method: string) => void
  clearAuthMethodHint?: () => void
}

/** GA4 計測ヒント。既存の signInGoogle と同じ作法（onAuthStateChanged 側が消費する） */
function gtm(): GtmFacade | null {
  const client = getFirebaseClient() as { $gtm?: GtmFacade } | null
  return client?.$gtm ?? null
}

async function signInWithGoogleCredential(idToken: string): Promise<void> {
  const credential = createGoogleCredential(idToken)
  const result = await signInWithCredential(getFirebaseAuth(), credential)
  if (result.user) {
    // redirect を通らないため getRedirectResult 相当の後処理を明示的に行う
    const { runLoginPostProcess } = useLoginPostProcess()
    await runLoginPostProcess(result.user)
  }
}

let signOutWatchInstalled = false

/** ログアウト直後に One Tap が勝手に再ログインしないようにする */
function ensureDisableAutoSelectOnSignOut(): void {
  if (signOutWatchInstalled) return
  signOutWatchInstalled = true
  const { user } = useAuth()
  watch(user, (next, prev) => {
    if (prev && !next) {
      getGoogleAccountsId()?.disableAutoSelect()
    }
  })
}

type PromptOneTapResult = 'prompted' | 'unavailable'

/** 表示中の One Tap プロンプトを閉じる（未表示・未ロードなら何もしない） */
export function cancelOneTapPrompt(): void {
  const w = window as unknown as {
    google?: { accounts?: { id?: { cancel?: () => void } } }
  }
  w.google?.accounts?.id?.cancel?.()
}

export type UseGoogleOneTapReturn = {
  /** クライアント ID が設定されていて実行可能か */
  isConfigured: () => boolean
  /**
   * One Tap プロンプトを表示する。
   * onNotShown は「プロンプトが出なかった / ユーザーがスキップした」ときに一度だけ呼ばれる
   * （自前カードへのフォールバック用）。FedCM ではモーメント通知自体が来ないことがあるため、
   * 呼び出し側は通知だけに依存せずタイムアウト等でもフォールバックさせること。
   * onSignInError は credential 取得後の Firebase ログイン失敗時に呼ばれる
   */
  promptOneTap: (options?: {
    onNotShown?: () => void
    onSignedIn?: () => void
    onSignInError?: () => void
  }) => Promise<PromptOneTapResult>
}

export function useGoogleOneTap(): UseGoogleOneTapReturn {
  ensureDisableAutoSelectOnSignOut()

  const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID as string | undefined

  function isConfigured(): boolean {
    return !!clientId
  }

  async function promptOneTap(options?: {
    onNotShown?: () => void
    onSignedIn?: () => void
    onSignInError?: () => void
  }): Promise<PromptOneTapResult> {
    if (!clientId) return 'unavailable'
    try {
      await loadGsiScript()
    } catch (e) {
      console.error('[useGoogleOneTap] script load failed', e)
      return 'unavailable'
    }
    const accountsId = getGoogleAccountsId()
    if (!accountsId) return 'unavailable'

    let settled = false
    accountsId.initialize({
      client_id: clientId,
      // GoogleAuthProvider.credential は rawNonce を渡せないため nonce は設定しない
      use_fedcm_for_prompt: true,
      cancel_on_tap_outside: false,
      context: 'signin',
      callback: async (response: { credential?: string }) => {
        if (!response?.credential) return
        settled = true
        // リダイレクトしない経路だが、onAuthStateChanged 側の method 判定へ渡す作法を揃える
        gtm()?.hintAuthMethod?.('google')
        try {
          await signInWithGoogleCredential(response.credential)
          options?.onSignedIn?.()
        } catch (e) {
          // 古いヒントが残ると次回別手段のログインが google で計上されるため捨てる
          gtm()?.clearAuthMethodHint?.()
          console.error('[useGoogleOneTap] signInWithCredential failed', e)
          // プロンプトは既に消えているため、再試行導線（自前カード等）へ渡す
          options?.onSignInError?.()
        }
      }
    })
    accountsId.prompt((notification) => {
      if (settled) return
      const notShown =
        notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()
      if (notShown) {
        settled = true
        options?.onNotShown?.()
      }
    })
    return 'prompted'
  }

  return { isConfigured, promptOneTap }
}
